import { useState } from 'react';
import { IconType } from 'components/Icon';
import Icon from 'components/Icon';
import Button, { ButtonType } from 'components/Widget/Button/Button';
import {
	Navbar,
	NavbarLeft,
	NavbarCenter,
	NavbarRight,
	NavbarButton,
	NavbarTitle,
} from 'components/Widget/Navbar';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { MenuItemAction } from 'menu/MenuItem';
import NavTab from './NavTab';
import * as S from './styles';
import { RootReducer } from 'redux/reducers';
import { selectActiveDesignSystem, selectActiveDesignSystemVersion } from 'redux/db/reducers';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { showConfirmModal } from 'redux/mode/actions';
import forge from 'apis/forge';
import { toast } from 'react-toastify';
import { Loader } from 'components/Widget/Loader';
import { Tooltip } from 'components/Widget/Tooltip';
import ShareDesignSystemModal from 'components/Modal/Share/ShareDesignSystemModal';
import { trackDesignSystemAction } from 'analytics';
import JSZip from 'jszip';
import { downloadBlob } from 'utils';

interface TopBarProps {
	activeSection: 'tokens' | 'styles' | 'components' | 'stories';
	onSectionClick: (section: 'tokens' | 'styles' | 'components' | 'stories') => void;
	onToggleSidebar: () => void;
}

const TopBar = ({ activeSection, onSectionClick, onToggleSidebar }: TopBarProps) => {
	const dispatch = useDispatch();
	const {
		sidebarVisible,
		activeDesignSystem,
		activeDesignSystemVersion,
		latestPublishedVersionNumber,
		designSystemContent,
	} = useSelector(
		(r: RootReducer) => ({
			sidebarVisible: r.mode.sidebarVisible,
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			activeDesignSystemVersion: selectActiveDesignSystemVersion(r.db),
			latestPublishedVersionNumber: r.db.latestPublishedVersionNumber,
			designSystemContent: r.mode.designSystemContent,
		}),
		shallowEqual
	);
	const [spinnerTooltipId] = useState(String(Math.random()));
	const [errorTooltipId] = useState(String(Math.random()));
	const [showShareModal, setShowShareModal] = useState(false);

	const handleDiscardClick = () => {
		if (!activeDesignSystem?.id) return;

		dispatch(
			showConfirmModal({
				title: 'Discard Changes?',
				description:
					'Are you sure you want to discard all draft changes? This action cannot be undone.',
				cancelLabel: 'Cancel',
				submitLabel: 'Discard Changes',
				onSubmit: async () => {
					try {
						await forge.discardDsVersion(activeDesignSystem.id);
						toast.success('Draft changes discarded');
					} catch (error) {
						toast.error('Failed to discard changes');
					}
				},
			})
		);
	};

	const handlePublishClick = () => {
		if (!activeDesignSystem?.id) return;

		dispatch(
			showConfirmModal({
				title: 'Publish Design System?',
				description:
					'Are you sure you want to publish this design system version? This will create a new published version and discard the draft.',
				cancelLabel: 'Cancel',
				submitLabel: 'Publish',
				onSubmit: async () => {
					try {
						const response = await forge.publishDsVersion(activeDesignSystem.id);
						toast.success(`Design system published as v${response.versionNumber}`);
					} catch (error) {
						toast.error('Failed to publish design system');
					}
				},
			})
		);
	};

	const handleDownloadKit = async () => {
		if (!designSystemContent?.rawCode) {
			toast.error('No design system content available to download');
			return;
		}

		const { rawCode } = designSystemContent;
		const hasContent =
			rawCode.tokens || rawCode.primitives || rawCode.components || rawCode.stories;
		if (!hasContent) {
			toast.error('No design system content available to download');
			return;
		}

		const zip = new JSZip();

		if (rawCode.tokens) {
			zip.file('tokens.css', rawCode.tokens);
		}
		if (rawCode.primitives) {
			zip.file('primitives.css', rawCode.primitives);
		}
		if (rawCode.components) {
			zip.file('components.js', rawCode.components);
		}
		if (rawCode.stories) {
			zip.file('stories.txt', rawCode.stories);
		}

		try {
			const zipBlob = await zip.generateAsync({ type: 'blob' });
			const trimmedTitle = activeDesignSystem?.title?.trim();
			const filename = trimmedTitle
				? `${trimmedTitle.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-kit.zip`
				: 'ds-kit-contents.zip';
			downloadBlob(zipBlob, filename);
			toast.success('Design system kit downloaded');
		} catch (error) {
			toast.error('Failed to create download file');
		}
	};

	const versionNumber = activeDesignSystemVersion?.versionNumber ?? null;
	const isDraft = versionNumber === 0 && latestPublishedVersionNumber !== null;
	const isUnpublished = versionNumber === 0 && latestPublishedVersionNumber === null;
	const promptGenerationStatus = activeDesignSystemVersion?.manifest?.promptGenerationStatus;
	const showSpinner =
		promptGenerationStatus === 'pending' || promptGenerationStatus === 'generating';
	const showError = promptGenerationStatus === 'failed';

	const spinnerTooltipMessage = 'Prompt generation is in progress';
	const errorTooltipMessage = 'Prompt generation failed';

	return (
		<>
			<Navbar>
				<NavbarLeft>
					<NavbarTitle>{activeDesignSystem?.title || 'Untitled Design System'}</NavbarTitle>
				</NavbarLeft>

				<NavbarCenter>
					<NavTab
						icon={IconType.LAYOUT}
						label="Tokens"
						active={activeSection === 'tokens'}
						onClick={() => onSectionClick('tokens')}
					/>
					<NavTab
						icon={IconType.STYLE}
						label="Primitives"
						active={activeSection === 'styles'}
						onClick={() => onSectionClick('styles')}
					/>
					<NavTab
						icon={IconType.COMPONENT}
						label="Components"
						active={activeSection === 'components'}
						onClick={() => onSectionClick('components')}
					/>
					<NavTab
						icon={IconType.FILE}
						label="Stories"
						active={activeSection === 'stories'}
						onClick={() => onSectionClick('stories')}
					/>
				</NavbarCenter>

				<NavbarRight>
					{isDraft ? (
						<DropdownMenu
							menuItems={[
								new MenuItemAction('Publish', '', handlePublishClick),
								new MenuItemAction('Discard Changes', '', handleDiscardClick),
							]}
						>
							<S.VersionDropdown>
								{`v${latestPublishedVersionNumber} (draft)`}
								{(showSpinner || showError) && (
									<S.StatusIndicator>
										{showSpinner && (
											<>
												<a
													data-tooltip-id={spinnerTooltipId}
													data-tooltip-content={spinnerTooltipMessage}
												>
													<Loader width={14} height={14} />
												</a>
												<Tooltip id={spinnerTooltipId} placement="bottom" />
											</>
										)}
										{showError && (
											<>
												<a
													data-tooltip-id={errorTooltipId}
													data-tooltip-content={errorTooltipMessage}
												>
													<Icon type={IconType.ERROR} width={14} height={14} />
												</a>
												<Tooltip id={errorTooltipId} placement="bottom" />
											</>
										)}
									</S.StatusIndicator>
								)}
								<Icon type={IconType.FILLED_ARROW_DOWN} width={12} height={12} />
							</S.VersionDropdown>
						</DropdownMenu>
					) : (
						<S.VersionText>
							{isUnpublished ? 'Unpublished' : `v${versionNumber ?? '?'}`}
							{(showSpinner || showError) && (
								<S.StatusIndicator>
									{showSpinner && (
										<>
											<a
												data-tooltip-id={spinnerTooltipId}
												data-tooltip-content={spinnerTooltipMessage}
											>
												<Loader width={14} height={14} />
											</a>
											<Tooltip id={spinnerTooltipId} placement="bottom" />
										</>
									)}
									{showError && (
										<>
											<a
												data-tooltip-id={errorTooltipId}
												data-tooltip-content={errorTooltipMessage}
											>
												<Icon type={IconType.ERROR} width={14} height={14} />
											</a>
											<Tooltip id={errorTooltipId} placement="bottom" />
										</>
									)}
								</S.StatusIndicator>
							)}
						</S.VersionText>
					)}
					<S.Share>
						<NavbarButton
							icon={IconType.LINE_ARROW_DOWN}
							onClick={handleDownloadKit}
							tooltip="Download Kit"
							tooltipId="download-kit-tooltip"
						/>
					</S.Share>
					<S.Share>
						<Button
							onSelected={() => {
								trackDesignSystemAction('open_share_modal');
								setShowShareModal(true);
							}}
							type={ButtonType.NOTICEABLE}
						>
							Share
						</Button>
					</S.Share>
				</NavbarRight>
			</Navbar>
			{showShareModal && activeDesignSystem && (
				<ShareDesignSystemModal
					designSystem={activeDesignSystem}
					onClose={() => setShowShareModal(false)}
				/>
			)}
		</>
	);
};

export default TopBar;
